// ============================================================================
// TrendChart — a vivid, dependency-free SVG area+line chart for a measurement
// series, matching the dashboard's ProgressSnapshot language (animated path
// draw, gradient fill, end-dot) but generalized:
//   • per-accent gradient (coral / violet / ocean / lime / amber)
//   • optional horizontal THRESHOLD reference line (for WHR / WHtR ceilings)
//   • optional GOAL reference line (target value)
//   • reduced-motion aware (no path-draw tween)
// One point renders as a single dot; <2 points render nothing (caller shows an
// empty state) so we never draw a misleading flat line.
// ============================================================================
import { useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { SeriesPoint } from "../../api";
import { fmtDate, fmtNum } from "./lib";

export type ChartAccent = "coral" | "violet" | "ocean" | "lime" | "amber";

const ACCENT_STOPS: Record<ChartAccent, [string, string]> = {
  coral: ["#FF5E62", "#FF9966"],
  violet: ["#6A5BFF", "#9D6BFF"],
  ocean: ["#2D8FFF", "#5B6BFF"],
  lime: ["#18C964", "#3BE085"],
  amber: ["#FF8A3D", "#F5A524"],
};

export interface RefLine {
  value: number;
  label: string;
  /** Visual treatment: a risk ceiling vs. a goal target. */
  kind?: "threshold" | "goal";
}

export interface TrendChartProps {
  series: SeriesPoint[];
  accent?: ChartAccent;
  decimals?: number;
  suffix?: string;
  /** Horizontal reference lines (thresholds / goal target). */
  refLines?: RefLine[];
  ariaLabel: string;
  height?: number;
}

const W = 560;
const PAD = { top: 20, right: 18, bottom: 26, left: 40 };

export function TrendChart({
  series,
  accent = "coral",
  decimals = 1,
  suffix = "",
  refLines = [],
  ariaLabel,
  height = 200,
}: TrendChartProps) {
  const reduce = useReducedMotion();
  const gradId = useId();
  const H = height;

  const model = useMemo(() => {
    if (series.length < 1) return null;
    const ys = series.map((p) => p.y);
    const refVals = refLines.map((r) => r.value);
    const lo = Math.min(...ys, ...refVals);
    const hi = Math.max(...ys, ...refVals);
    const span = hi - lo;
    // Pad the domain so the line never hugs the frame; guard the flat case.
    const padY = span === 0 ? Math.max(Math.abs(hi) * 0.1, 1) : span * 0.15;
    const domLo = lo - padY;
    const domHi = hi + padY;
    const range = domHi - domLo || 1;

    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const x = (i: number) =>
      series.length === 1
        ? PAD.left + innerW / 2
        : PAD.left + (innerW * i) / (series.length - 1);
    const y = (v: number) => PAD.top + innerH * (1 - (v - domLo) / range);

    const linePath = series
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`)
      .join(" ");
    const areaPath =
      series.length >= 2
        ? `${linePath} L ${x(series.length - 1).toFixed(1)} ${(H - PAD.bottom).toFixed(
            1,
          )} L ${x(0).toFixed(1)} ${(H - PAD.bottom).toFixed(1)} Z`
        : "";
    const ticks = [domHi, (domHi + domLo) / 2, domLo];

    return {
      x,
      y,
      linePath,
      areaPath,
      ticks,
      lastX: x(series.length - 1),
      lastY: y(ys[ys.length - 1]),
      firstX: x(0),
    };
  }, [series, refLines, H]);

  if (!model) return null;
  const [c0, c1] = ACCENT_STOPS[accent];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="block w-full"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`${gradId}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c0} stopOpacity="0.26" />
          <stop offset="100%" stopColor={c1} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${gradId}-line`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={c0} />
          <stop offset="100%" stopColor={c1} />
        </linearGradient>
      </defs>

      {/* Y grid + labels */}
      {model.ticks.map((tick, i) => (
        <g key={`t${i}`}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={model.y(tick)}
            y2={model.y(tick)}
            stroke="#EAEDF5"
            strokeWidth="1"
          />
          <text
            x={PAD.left - 8}
            y={model.y(tick) + 4}
            textAnchor="end"
            className="fill-body-faint text-[10px] font-bold"
          >
            {fmtNum(tick, decimals)}
          </text>
        </g>
      ))}

      {/* Reference lines (threshold ceilings / goal target) */}
      {refLines.map((ref, i) => {
        const isGoal = ref.kind === "goal";
        const color = isGoal ? "#7B5BFF" : "#F5A524";
        return (
          <g key={`r${i}`}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={model.y(ref.value)}
              y2={model.y(ref.value)}
              stroke={color}
              strokeWidth="1.5"
              strokeDasharray="5 5"
              opacity="0.8"
            />
            <text
              x={W - PAD.right}
              y={model.y(ref.value) - 5}
              textAnchor="end"
              className="text-[10px] font-bold"
              fill={color}
            >
              {ref.label}
            </text>
          </g>
        );
      })}

      {/* Area */}
      {model.areaPath && (
        <motion.path
          d={model.areaPath}
          fill={`url(#${gradId}-area)`}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: reduce ? 0 : 0.25 }}
        />
      )}

      {/* Line */}
      {series.length >= 2 && (
        <motion.path
          d={model.linePath}
          fill="none"
          stroke={`url(#${gradId}-line)`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
        />
      )}

      {/* End dot */}
      <motion.circle
        cx={model.lastX}
        cy={model.lastY}
        r="5"
        fill="#fff"
        stroke={c0}
        strokeWidth="3"
        initial={reduce ? false : { scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: reduce ? 0 : series.length >= 2 ? 1.0 : 0.2,
          type: "spring",
          stiffness: 400,
          damping: 18,
        }}
        style={{ transformOrigin: "center", transformBox: "fill-box" }}
      />

      {/* X end labels (first + last date) */}
      {series.length >= 2 && (
        <>
          <text
            x={model.firstX}
            y={H - 8}
            textAnchor="start"
            className="fill-body-faint text-[10px] font-medium"
          >
            {fmtDate(series[0].x)}
          </text>
          <text
            x={model.lastX}
            y={H - 8}
            textAnchor="end"
            className="fill-body-faint text-[10px] font-medium"
          >
            {fmtDate(series[series.length - 1].x)}
          </text>
        </>
      )}

      {/* end value annotation */}
      <text
        x={model.lastX}
        y={model.lastY - 12}
        textAnchor="end"
        className="font-display text-[11px] font-extrabold"
        fill={c0}
      >
        {fmtNum(series[series.length - 1].y, decimals)}
        {suffix}
      </text>
    </svg>
  );
}
